// In-app menu bar for the web build — the same menu model the Electron
// application menu is built from (src/shared/menus.ts), so the browser app keeps
// the same commands and keyboard accelerators.
//
// Each item fires the SAME channel the desktop's OS menu sends
// (mainWindow.webContents.send('menu:…')). In the browser those channels are
// delivered as `stenal-menu:<channel>` DOM events, which webPlatform.onMenu
// bridges to the renderer's platform.onMenu(...) handlers. So the entire
// existing menu wiring in main.ts works unchanged.
//
// Desktop builds never import this module (only the web entry does), and the
// native OS menu keeps serving there.

import {
  logout,
  refreshPermissions,
} from './webAuth';
import { canEditStandards } from './webPlatform';
import {
  getUiLang,
  tr,
  UI_LANG_NAMES,
} from './i18n';
import { showAlert } from './dialog';
import {
  matchesShortcut,
  type ShortcutId,
  shortcutLabel,
} from '../shared/shortcuts';
import {
  type MenuEntry,
  menusFor,
  RENDERER_BOUND,
} from '../shared/menus';

declare const __APP_VERSION__: string; // injected by esbuild from package.json

// Menu items that edit shared standards (catalogue / colours / title blocks /
// benches). Hidden from a read-only user (one without the stenal.net `edit`
// claim), so they can view but not change the standards.
const STANDARDS_EDIT_CHANNELS = new Set([
  'menu:newProduct', 'menu:colorEditor', 'menu:cartigli', 'menu:benchPresets', 'menu:steamBenchPresets', 'menu:saunaProfiles', 'menu:pipeTypes', 'menu:importHeater',
]);
function itemHidden(it: Item): boolean {
  return ('role' in it) || ('channel' in it && STANDARDS_EDIT_CHANNELS.has(it.channel) && !canEditStandards());
}

type Item = MenuEntry;

// Built from the ONE menu model (src/shared/menus.ts) the desktop menu reads too.
// The Language menu lists the UI languages itself, in their own names.
const MENUS: Array<{ label: string; items: Item[] }> = menusFor('web').map((m) => (m.languages
  ? { label: m.label, items: (['en', 'it', 'fr'] as const).map((l) => ({ label: UI_LANG_NAMES[l], channel: `menu:lang-${l}` })) }
  : { label: m.label, items: m.items }));

// Channels beginning with '@' are handled locally in the browser (there is no
// Node/main process to send them to); everything else is a real menu:* channel.
function fire(channel: string): void {
  if (channel === '@reload') { location.reload(); return; }
  if (channel === '@fullscreen') {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void document.documentElement.requestFullscreen();
    return;
  }
  if (channel === '@about') {
    void showAlert(`StenalCAD v${__APP_VERSION__}\n\n${tr('Technical drawing tool for placing prefabricated saunas, steam rooms and showers over imported DXF/DWG plans.')}`, 'About StenalCAD');
    return;
  }
  if (channel === '@logout') { void logout(); return; }
  if (channel === '@updatePermissions') { void updatePermissions(); return; }
  window.dispatchEvent(new Event(`stenal-menu:${channel}`));
}

/** Re-check permissions with stenal.net and apply them without a re-login: rebuild
 *  the menu bar (edit-only items) and tell the renderer to refresh its permission-
 *  gated UI (via a `stenal-permissions` event). */
async function updatePermissions(): Promise<void> {
  const res = await refreshPermissions();
  if (res.status === 'offline') { void showAlert(tr('Could not reach the server to update permissions.')); return; }
  if (res.status === 'revoked') {
    await showAlert(tr('Your access has changed. Please sign in again.'));
    void logout(); // clears the session and reloads to the login gate
    return;
  }
  buildBar(); // reflect any edit-claim change in the menu
  window.dispatchEvent(new Event('stenal-permissions')); // renderer re-renders its gated UI
  void showAlert(res.changed ? tr('Permissions updated.') : tr('Permissions are up to date.'));
}

const CSS = `
.webmenu { display:flex; align-items:stretch; height:26px; background:#232323; color:#ddd;
  font:13px system-ui,Segoe UI,sans-serif; user-select:none; border-bottom:1px solid #111; position:relative; z-index:5000; }
.webmenu-top { position:relative; }
.webmenu-top > button { height:26px; padding:0 10px; background:transparent; border:0; color:inherit;
  font:inherit; cursor:default; }
.webmenu-top.open > button, .webmenu-top > button:hover { background:#3a3a3a; }
.webmenu-pop { position:absolute; top:26px; left:0; min-width:220px; background:#2b2b2b;
  border:1px solid #111; box-shadow:0 6px 20px rgba(0,0,0,.5); padding:4px 0; display:none; z-index:5001; }
.webmenu-top.open > .webmenu-pop { display:block; }
.webmenu-item { display:flex; align-items:center; justify-content:space-between; gap:24px;
  padding:5px 14px; white-space:nowrap; cursor:default; position:relative; }
.webmenu-item:hover { background:#3d7eff; color:#fff; }
.webmenu-item .accel { opacity:.6; font-size:12px; }
.webmenu-item:hover .accel { opacity:.85; }
.webmenu-item.has-sub::after { content:'▸'; opacity:.7; }
.webmenu-sep { height:1px; background:#444; margin:4px 8px; }
.webmenu-sub { position:absolute; top:-4px; left:100%; min-width:200px; background:#2b2b2b;
  border:1px solid #111; box-shadow:0 6px 20px rgba(0,0,0,.5); padding:4px 0; display:none; }
.webmenu-item.has-sub:hover > .webmenu-sub { display:block; }
.webmenu-version { display:flex; align-items:center; padding:0 10px; color:#888; font-size:12px; }
.webmenu-spacer { flex:1; }
.webmenu-project { display:flex; align-items:center; padding:0 12px; max-width:40vw;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis; color:#bbb; font-weight:600; }
`;

function makeItemRow(it: Item, closeAll: () => void): HTMLElement {
  if ('sep' in it || 'role' in it) { const s = document.createElement('div'); s.className = 'webmenu-sep'; return s; }
  const row = document.createElement('div');
  row.className = 'webmenu-item';
  const label = document.createElement('span');
  // Language items keep their own-language name (not translated) and mark the
  // active language with a check; everything else is translated to the UI language.
  const isLang = 'channel' in it && it.channel.startsWith('menu:lang-');
  if (isLang) {
    const active = it.channel === `menu:lang-${getUiLang()}`;
    label.textContent = (active ? '✓ ' : ' ') + it.label;
  } else {
    label.textContent = tr(it.label);
  }
  row.appendChild(label);
  if ('submenu' in it) {
    row.classList.add('has-sub');
    const sub = document.createElement('div');
    sub.className = 'webmenu-sub';
    for (const child of it.submenu) if (!itemHidden(child)) sub.appendChild(makeItemRow(child, closeAll));
    row.appendChild(sub);
  } else if ('channel' in it) { // (a desktop-only recent-files list never reaches the web bar)
    if (it.shortcut) { const a = document.createElement('span'); a.className = 'accel'; a.textContent = shortcutLabel(it.shortcut, tr); row.appendChild(a); }
    row.addEventListener('click', (e) => { e.stopPropagation(); closeAll(); fire(it.channel); });
  }
  return row;
}

let webMenuStarted = false;

/** Build (or rebuild) the menu bar itself, translating labels to the current
 *  UI language. Replaces any existing bar in place. */
function buildBar(): void {
  document.querySelector('.webmenu')?.remove();
  const bar = document.createElement('div');
  bar.className = 'webmenu';
  const tops: HTMLElement[] = [];
  const closeAll = (): void => { for (const t of tops) t.classList.remove('open'); };
  for (const menu of MENUS) {
    const top = document.createElement('div');
    top.className = 'webmenu-top';
    const btn = document.createElement('button');
    btn.textContent = tr(menu.label);
    const pop = document.createElement('div');
    pop.className = 'webmenu-pop';
    for (const it of menu.items) if (!itemHidden(it)) pop.appendChild(makeItemRow(it, closeAll));
    top.append(btn, pop);
    // Click toggles; hovering another top while one is open switches to it.
    btn.addEventListener('click', (e) => { e.stopPropagation(); const wasOpen = top.classList.contains('open'); closeAll(); if (!wasOpen) top.classList.add('open'); });
    top.addEventListener('mouseenter', () => { if (tops.some((t) => t.classList.contains('open'))) { closeAll(); top.classList.add('open'); } });
    tops.push(top);
    bar.appendChild(top);
  }
  // The app version, right after the last menu.
  const version = document.createElement('div');
  version.className = 'webmenu-version';
  version.textContent = `v${__APP_VERSION__}`;
  bar.appendChild(version);
  // Spacer, then the current project (file) name on the right. main.ts keeps it in
  // sync via updateTitle(); we seed it from <body> so a language-switch rebuild
  // doesn't blank it.
  const spacer = document.createElement('div');
  spacer.className = 'webmenu-spacer';
  const project = document.createElement('div');
  project.className = 'webmenu-project';
  project.id = 'webmenu-project';
  project.textContent = document.body.dataset.projectName || '';
  bar.append(spacer, project);
  bar.dataset.closeAll = '1';
  document.body.insertBefore(bar, document.body.firstChild);
  document.addEventListener('click', closeAll);
}

export function mountWebMenu(): void {
  if (webMenuStarted) { buildBar(); return; }
  webMenuStarted = true;

  const style = document.createElement('style');
  // Shrink the app grid so the fixed-height menu bar sits above it without scroll.
  style.textContent = CSS + '\n#app { height: calc(100vh - 26px) !important; }';
  document.head.appendChild(style);

  buildBar();
  installAccelerators();
  // A UI-language switch re-translates the menu bar in place.
  window.addEventListener('stenal-uilang', () => buildBar());
}

// Keyboard accelerators for the menu items whose keys the renderer doesn't bind
// itself: the chords come from the menu items' own `shortcut`, i.e. from the one
// table, so a menu label and the key that fires it cannot drift apart. Undo/redo,
// copy/paste, Delete and the help sheet are bound in the renderer for both builds
// (they must also work with no menu at all) — registering them here too would fire
// them twice. Nothing fires while the user is typing in a field, so native text
// editing keeps Ctrl+Z etc.
function isEditable(el: EventTarget | null): boolean {
  const n = el as HTMLElement | null;
  if (!n) return false;
  const tag = n.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || n.isContentEditable;
}

function menuAccelerators(items: Item[], out: Array<{ id: ShortcutId; channel: string }> = []): Array<{ id: ShortcutId; channel: string }> {
  for (const it of items) {
    if ('submenu' in it) menuAccelerators(it.submenu, out);
    else if ('shortcut' in it && it.shortcut && !RENDERER_BOUND.has(it.shortcut)) out.push({ id: it.shortcut, channel: it.channel });
  }
  return out;
}

function installAccelerators(): void {
  const accels = MENUS.flatMap((m) => menuAccelerators(m.items));
  window.addEventListener('keydown', (e) => {
    if (isEditable(e.target)) return;
    for (const a of accels) {
      if (!matchesShortcut(e, a.id)) continue;
      e.preventDefault();
      fire(a.channel);
      return;
    }
  });
}
